import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, money, PLACEHOLDER_IMG } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/ImageUpload";
import { AdminProductForm } from "@/components/admin/AdminProductForm";
import { AdminBlogForm } from "@/components/admin/AdminBlogForm";
import { ProgramConfigEditor } from "@/components/ProgramConfigEditor";
import { LogOut, Plus, Trash2, Package, ShoppingCart, Mail, Gift, Pencil, Newspaper, Video, Printer, Sparkles, BellRing, Users, Heart, X, Download, ShieldCheck, Check, HeartHandshake } from "lucide-react";

const TABS = [["products", "Products", Package], ["byob", "Box Items", Gift], ["programs", "Programs", Users], ["blog", "Blog", Newspaper], ["video", "Video", Video], ["audience", "Audience", Users], ["orders", "Orders", ShoppingCart], ["contacts", "Messages", Mail]];

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [byob, setByob] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [blogForm, setBlogForm] = useState({ title: "", excerpt: "", body: "", image: "", video_url: "" });
  const [editingPostId, setEditingPostId] = useState(null);
  const [welcomeVideo, setWelcomeVideo] = useState("");
  const [impactFamilies, setImpactFamilies] = useState(0);
  const [graceImg, setGraceImg] = useState("");
  const [warmPhotos, setWarmPhotos] = useState([]);
  const [schoolPhotos, setSchoolPhotos] = useState([]);
  const [schoolColors, setSchoolColors] = useState([]);
  const saveKitPhotos = async (which, list) => {
    if (which === "warm") setWarmPhotos(list); else setSchoolPhotos(list);
    try {
      await api.put("/admin/settings", {
        welcome_video_url: welcomeVideo,
        grace_warm_photos: which === "warm" ? list : warmPhotos,
        grace_school_photos: which === "school" ? list : schoolPhotos,
      });
      toast.success("Kit photos updated 💛");
    } catch { toast.error("Could not save photos."); }
  };
  const persistSchool = async (photos, colors) => {
    try {
      await api.put("/admin/settings", { welcome_video_url: welcomeVideo, grace_school_photos: photos, grace_school_colors: colors });
      toast.success("School kit colors updated 💛");
    } catch { toast.error("Could not save."); }
  };
  const addSchoolOption = (url) => {
    if (!url) return;
    const p = [...schoolPhotos, url];
    const c = [...schoolColors, `Color ${schoolPhotos.length + 1}`];
    setSchoolPhotos(p); setSchoolColors(c); persistSchool(p, c);
  };
  const removeSchoolOption = (i) => {
    const p = schoolPhotos.filter((_, j) => j !== i);
    const c = schoolColors.filter((_, j) => j !== i);
    setSchoolPhotos(p); setSchoolColors(c); persistSchool(p, c);
  };
  const setSchoolColorName = (i, val) => setSchoolColors((prev) => prev.map((c, j) => (j === i ? val : c)));
  const [hopeLive, setHopeLive] = useState(false);
  const [waitlist, setWaitlist] = useState({ total: 0, pending: 0 });
  const [audience, setAudience] = useState({ total: 0, by_source: {}, queued: 0, sent: 0, subscribers: [] });
  const [impact, setImpact] = useState(null);
  const [impactRange, setImpactRange] = useState("all");
  const [programLeads, setProgramLeads] = useState([]);
  const loadImpact = (range) => api.get(`/admin/impact?range=${range}`).then((r) => setImpact(r.data)).catch(() => {});
  const selectImpactRange = (range) => { setImpactRange(range); loadImpact(range); };
  const exportImpactCsv = () => {
    if (!impact) return;
    const label = impactRange === "month" ? "This Month" : "All Time";
    const rows = [
      ["Metric", "Value"],
      ["Date range", label],
      ["Hope Kits sent", impact.kits_total],
      ["Warm kits", impact.kits_warm],
      ["School kits", impact.kits_school],
      ["Donations (USD)", impact.donations_total.toFixed(2)],
      ["Outreach orders", impact.outreach_orders],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hope-favor-impact-${impactRange}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Impact report downloaded");
  };
  const [pForm, setPForm] = useState({ name: "", collection_key: "cancer-illness", price: "", short_desc: "", description: "", reflection: "", image: "", images: [], digital_file: "", digital_filename: "", badge: "", product_type: "box", featured: false });
  const [editingId, setEditingId] = useState(null);
  const [bForm, setBForm] = useState({ name: "", price: "", category: "", description: "", image: "" });
  const [editingByobId, setEditingByobId] = useState(null);

  const loadAll = () => {
    api.get("/admin/products").then((r) => setProducts(r.data));
    api.get("/byob-items").then((r) => setByob(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/contacts").then((r) => setContacts(r.data));
    api.get("/admin/subscribers").then((r) => setAudience(r.data)).catch(() => {});
    api.get("/admin/impact").then((r) => setImpact(r.data)).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data));
    api.get("/admin/program-leads").then((r) => setProgramLeads(r.data)).catch(() => {});
  };

  const approveLead = async (id) => {
    const message = window.prompt("Optional note to include in the approval email:", "") ?? "";
    try {
      const { data } = await api.post(`/admin/program-leads/${id}/approve`, { message });
      toast.success(data.email_sent ? "Approved — payment link emailed to customer 💛" : "Approved (email not sent — check Resend)");
      loadAll();
    } catch { toast.error("Could not approve."); }
  };
  const denyLead = async (id) => {
    const message = window.prompt("Message to the customer (they can adjust & resubmit):", "We'd love to tailor a package that fits your budget — let's adjust a few details together.");
    if (message === null) return;
    try {
      const { data } = await api.post(`/admin/program-leads/${id}/deny`, { message });
      toast.success(data.email_sent ? "Response emailed to customer" : "Marked denied (email not sent)");
      loadAll();
    } catch { toast.error("Could not send."); }
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { api.get("/settings").then((r) => { setWelcomeVideo(r.data.welcome_video_url || ""); setImpactFamilies(r.data.impact_families || 0); setHopeLive(!!r.data.hope_cards_live); setGraceImg(r.data.grace_showcase_image || ""); setWarmPhotos(r.data.grace_warm_photos || []); setSchoolPhotos(r.data.grace_school_photos || []); setSchoolColors(r.data.grace_school_colors || []); }); }, []);
  const loadWaitlist = () => api.get("/admin/hope-cards/waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
  useEffect(() => { loadWaitlist(); }, []);

  const launchHopeCards = async () => {
    if (!window.confirm("Make Hope Cards live and email everyone on the waitlist?")) return;
    try {
      const { data } = await api.post("/admin/hope-cards/launch");
      setHopeLive(true); loadWaitlist();
      toast.success(`Hope Cards are live! Notified ${data.sent} subscriber${data.sent === 1 ? "" : "s"}. 💛`);
    } catch { toast.error("Could not launch Hope Cards."); }
  };
  const unpublishHopeCards = async () => {
    try { await api.post("/admin/hope-cards/unpublish"); setHopeLive(false); toast.success("Hope Cards set back to 'coming soon'."); }
    catch { toast.error("Could not update."); }
  };

  const announceProduct = async (p) => {
    if (!window.confirm(`Email all subscribers announcing "${p.name}"?`)) return;
    try {
      const { data } = await api.post(`/admin/broadcast/new-product/${p.id}`);
      toast.success(`Announced "${p.name}" to ${data.queued} subscriber${data.queued === 1 ? "" : "s"}. 💛`);
      loadWaitlist();
      api.get("/admin/subscribers").then((r) => setAudience(r.data)).catch(() => {});
    } catch { toast.error("Could not send the announcement."); }
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  const [genLoading, setGenLoading] = useState(false);
  const generateJournals = async () => {
    if (!window.confirm("Generate/refresh the 6 watercolor 90-day journals in this environment? Safe to run anytime.")) return;
    setGenLoading(true);
    try {
      const { data } = await api.post("/admin/generate-journals");
      toast.success(`${data.count} watercolor journals generated & published 💛`);
      api.get("/admin/products").then((r) => setProducts(r.data));
    } catch { toast.error("Could not generate journals."); }
    finally { setGenLoading(false); }
  };

  const [wmLoading, setWmLoading] = useState(false);
  const rewatermarkImages = async () => {
    if (!window.confirm("Add the baked-in copyright watermark to all product photos already uploaded to your store? This is safe to run anytime — already-watermarked images are skipped.")) return;
    setWmLoading(true);
    try {
      const { data } = await api.post("/admin/rewatermark");
      if (data.images_watermarked > 0) {
        toast.success(`Watermarked ${data.images_watermarked} image(s) across ${data.products_updated} product(s) 💛`);
      } else {
        toast.info("No uploaded product photos needed watermarking yet. Upload your own product photos first — new uploads are watermarked automatically.");
      }
      loadAll();
    } catch { toast.error("Could not re-watermark images."); }
    finally { setWmLoading(false); }
  };

  const saveGraceImg = async (url) => {
    setGraceImg(url);
    try {
      await api.put("/admin/settings", { welcome_video_url: welcomeVideo, grace_showcase_image: url });
      toast.success("Hope in Action image updated 💛");
    } catch { toast.error("Could not save the image."); }
  };

  const printHopeCard = async (orderId) => {
    try {
      const res = await api.get(`/admin/orders/${orderId}/hope-card`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not generate the Hope Card PDF."); }
  };

  const printGiftReceipt = async (orderId) => {
    try {
      const res = await api.get(`/admin/orders/${orderId}/gift-receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not generate the gift receipt PDF."); }
  };

  const blankBlog = () => setBlogForm({ title: "", excerpt: "", body: "", image: "", video_url: "" });
  const startEditPost = (p) => {
    setEditingPostId(p.id);
    setBlogForm({ title: p.title, excerpt: p.excerpt || "", body: p.body || "", image: p.image || "", video_url: p.video_url || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEditPost = () => { setEditingPostId(null); blankBlog(); };
  const savePost = async (e) => {
    e.preventDefault();
    try {
      if (editingPostId) { await api.put(`/admin/blog/${editingPostId}`, blogForm); toast.success("Post updated"); }
      else { await api.post("/admin/blog", blogForm); toast.success("Post published 💛"); }
      setEditingPostId(null); blankBlog(); loadAll();
    } catch { toast.error("Could not save post"); }
  };
  const delPost = async (id) => { await api.delete(`/admin/blog/${id}`); toast.success("Post removed"); loadAll(); };

  const saveVideo = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/settings", { welcome_video_url: welcomeVideo }); toast.success("Welcome video saved 💛"); }
    catch { toast.error("Could not save video"); }
  };

  const saveImpact = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/settings", { welcome_video_url: welcomeVideo, impact_families: Math.max(0, parseInt(impactFamilies || 0, 10)) }); toast.success("Impact counter updated 💛"); }
    catch { toast.error("Could not save the number"); }
  };

  const blankProduct = () => setPForm({ name: "", collection_key: "cancer-illness", price: "", short_desc: "", description: "", reflection: "", image: "", images: [], digital_file: "", digital_filename: "", badge: "", product_type: "box", featured: false });

  const startEdit = (p) => {
    setEditingId(p.id);
    setPForm({ name: p.name, collection_key: p.collection_key, price: String(p.price), short_desc: p.short_desc || "", description: p.description || "", reflection: p.reflection || "", image: p.image || "", images: p.images || [], digital_file: p.digital_file || "", digital_filename: p.digital_filename || "", badge: p.badge || "", product_type: p.product_type, featured: !!p.featured });
    setTab("products");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditingId(null); blankProduct(); };

  const addGalleryImage = () => setPForm((prev) => ({ ...prev, images: [...(prev.images || []), ""] }));
  const setGalleryImage = (idx, url) => setPForm((prev) => ({ ...prev, images: prev.images.map((im, i) => i === idx ? url : im) }));
  const removeGalleryImage = (idx) => setPForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

  const addProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...pForm, price: parseFloat(pForm.price), images: (pForm.images || []).filter(Boolean) };
      if (editingId) { await api.put(`/admin/products/${editingId}`, payload); toast.success("Product updated"); }
      else { await api.post("/admin/products", payload); toast.success("Product added"); }
      setEditingId(null); blankProduct(); loadAll();
    } catch { toast.error("Could not save product"); }
  };
  const delProduct = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Removed"); loadAll(); };

  const addByob = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...bForm, price: parseFloat(bForm.price) };
      if (editingByobId) { await api.put(`/admin/byob-items/${editingByobId}`, payload); toast.success("Box item updated"); }
      else { await api.post("/admin/byob-items", payload); toast.success("Box item added"); }
      setEditingByobId(null); setBForm({ name: "", price: "", category: "", description: "", image: "" }); loadAll();
    } catch { toast.error("Could not save item"); }
  };
  const startEditByob = (b) => { setEditingByobId(b.id); setBForm({ name: b.name || "", price: String(b.price ?? ""), category: b.category || "", description: b.description || "", image: b.image || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancelEditByob = () => { setEditingByobId(null); setBForm({ name: "", price: "", category: "", description: "", image: "" }); };
  const delByob = async (id) => { await api.delete(`/admin/byob-items/${id}`); toast.success("Removed"); if (editingByobId === id) cancelEditByob(); loadAll(); };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-[#3A3632] text-white">
        <div className="max-w-6xl mx-auto px-6 h-18 py-4 flex items-center justify-between">
          <div><div className="font-heading text-2xl text-[#D9CBA0]">Hope &amp; Favor</div><div className="text-xs text-[#8A8175]">{user?.email}</div></div>
          <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 transition-colors"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-8 flex-wrap">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${tab === k ? "bg-[#8E9A86] text-white" : "bg-white border border-[#E5DFD5] text-[#3A3632]"}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-3 flex items-center justify-between bg-[#FBF7F0] border border-[#E5DFD5] rounded-xl px-5 py-3">
              <div className="text-sm text-[#6B655F]">Populate the 6 watercolor 90-day digital journals (covers + PDFs + products) in <strong>this</strong> environment. Run this once on your live site after deploying.</div>
              <button onClick={generateJournals} disabled={genLoading} data-testid="generate-journals-btn"
                className="shrink-0 inline-flex items-center gap-2 bg-[#8E9A86] hover:bg-[#7A8772] disabled:opacity-60 text-white rounded-full px-5 py-2.5 text-sm font-medium">
                {genLoading ? "Generating…" : "Generate / Refresh Journals"}
              </button>
            </div>
            <div className="lg:col-span-3 flex items-center justify-between bg-[#FBF7F0] border border-[#E5DFD5] rounded-xl px-5 py-3" data-testid="rewatermark-banner">
              <div className="text-sm text-[#6B655F]">Add the baked-in <strong>© Hope &amp; Favor</strong> copyright watermark to product photos you've already uploaded. New uploads are watermarked automatically; use this for older photos. Safe to run anytime.</div>
              <button onClick={rewatermarkImages} disabled={wmLoading} data-testid="rewatermark-btn"
                className="shrink-0 inline-flex items-center gap-2 bg-[#C4A4A4] hover:bg-[#B09090] disabled:opacity-60 text-white rounded-full px-5 py-2.5 text-sm font-medium">
                {wmLoading ? "Watermarking…" : "Watermark Existing Photos"}
              </button>
            </div>
            <div className="lg:col-span-3 bg-[#FBF7F0] border border-[#E5DFD5] rounded-xl px-5 py-4" data-testid="grace-image-editor">
              <div className="text-sm text-[#6B655F] mb-3">Hope in Action page image — the photo shown in the "Restoring dignity" band (under the kits). Upload a new one to replace it anytime.</div>
              <div className="max-w-md"><ImageUpload value={graceImg} onChange={saveGraceImg} testid="grace-image-upload" /></div>
            </div>
            {[["warm", "Warm Essentials Kit", warmPhotos]].map(([which, label, list]) => (
              <div key={which} className="lg:col-span-3 bg-[#FBF7F0] border border-[#E5DFD5] rounded-xl px-5 py-4" data-testid={`kit-photos-${which}`}>
                <div className="text-sm text-[#6B655F] mb-3"><strong>{label}</strong> photos — shown on the Hope in Action page (first photo is the main view). Add more photos or remove them.</div>
                <div className="flex flex-wrap gap-3 items-start">
                  {list.map((url, i) => (
                    <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#E5DFD5] bg-white">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => saveKitPhotos(which, list.filter((_, j) => j !== i))} data-testid={`kit-photo-remove-${which}-${i}`}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80" aria-label="Remove"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <div className="w-52"><ImageUpload value="" onChange={(url) => url && saveKitPhotos(which, [...list, url])} testid={`kit-photo-add-${which}`} /></div>
                </div>
              </div>
            ))}
            <div className="lg:col-span-3 bg-[#FBF7F0] border border-[#E5DFD5] rounded-xl px-5 py-4" data-testid="kit-photos-school">
              <div className="text-sm text-[#6B655F] mb-3"><strong>School Supply Backpack Kit</strong> — each option is a backpack <strong>color</strong> with its own photo. The color name appears in the customer's dropdown and on their order; the photo updates when they pick it. The first option is the main view.</div>
              <div className="flex flex-wrap gap-4 items-start">
                {schoolPhotos.map((url, i) => (
                  <div key={i} className="w-32" data-testid={`school-color-${i}`}>
                    <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-[#E5DFD5] bg-white">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeSchoolOption(i)} data-testid={`school-color-remove-${i}`}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80" aria-label="Remove"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <input value={schoolColors[i] || ""} onChange={(e) => setSchoolColorName(i, e.target.value)} onBlur={() => persistSchool(schoolPhotos, schoolColors)}
                      placeholder="Color name" data-testid={`school-color-name-${i}`}
                      className="w-full mt-2 border border-[#D1CCC5] rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-[#8E9A86]" />
                  </div>
                ))}
                <div className="w-52"><ImageUpload value="" onChange={addSchoolOption} testid="school-color-add" /></div>
              </div>
            </div>
            <AdminProductForm pForm={pForm} setPForm={setPForm} editingId={editingId} onSubmit={addProduct} onCancel={cancelEdit}
              addGalleryImage={addGalleryImage} setGalleryImage={setGalleryImage} removeGalleryImage={removeGalleryImage} />
            <div className="lg:col-span-2 space-y-3" data-testid="admin-products-list">
              {products.map((p) => (
                <div key={p.id} className={`bg-white rounded-xl border p-4 flex items-center gap-4 ${editingId === p.id ? "border-[#8E9A86]" : "border-[#E5DFD5]"}`}>
                  <img src={p.image || PLACEHOLDER_IMG} alt="" className="h-14 w-14 rounded-lg object-cover bg-[#F2EFEB]" />
                  <div className="flex-1">
                    <div className="font-medium text-[#3A3632] flex items-center gap-2">
                      {p.name}
                      {p.image_watermarked && (
                        <span data-testid={`product-protected-${p.slug}`} title="Copyright watermark baked into this photo"
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-[#8E9A86] bg-[#8E9A86]/12 border border-[#8E9A86]/30 rounded-full px-2 py-0.5">
                          <ShieldCheck className="h-3 w-3" /> Protected
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[#8A8175]">{p.collection_key} · {money(p.price)} {p.featured && "· ★"}</div>
                  </div>
                  {p.product_type === "box" && (
                    <button onClick={() => announceProduct(p)} data-testid={`announce-product-${p.slug}`} title="Announce to subscribers" className="text-[#8A8175] hover:text-[#8E9A86]"><BellRing className="h-4 w-4" /></button>
                  )}
                  <button onClick={() => startEdit(p)} data-testid={`edit-product-${p.slug}`} className="text-[#8A8175] hover:text-[#8E9A86]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => delProduct(p.id)} data-testid={`del-product-${p.slug}`} className="text-[#8A8175] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "byob" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <form onSubmit={addByob} data-testid="admin-byob-form" className={`bg-white rounded-xl border p-6 space-y-3 h-fit ${editingByobId ? "border-[#8E9A86]" : "border-[#E5DFD5]"}`}>
              <h3 className="font-heading text-xl text-[#3A3632] flex items-center gap-2">{editingByobId ? <><Pencil className="h-4 w-4" /> Edit Box Item</> : <><Plus className="h-4 w-4" /> New Box Item</>}</h3>
              <input required placeholder="Name" value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} className="ai" data-testid="bf-name" />
              <input required type="number" step="0.01" placeholder="Price" value={bForm.price} onChange={(e) => setBForm({ ...bForm, price: e.target.value })} className="ai" data-testid="bf-price" />
              <input placeholder="Category" value={bForm.category} onChange={(e) => setBForm({ ...bForm, category: e.target.value })} className="ai" />
              <ImageUpload value={bForm.image} onChange={(url) => setBForm({ ...bForm, image: url })} testid="bf-image" />
              <input placeholder="Description" value={bForm.description} onChange={(e) => setBForm({ ...bForm, description: e.target.value })} className="ai" />
              <button type="submit" data-testid="bf-submit" className="w-full bg-[#8E9A86] text-white rounded-full py-2.5 font-medium hover:bg-[#7A8772]">{editingByobId ? "Save Changes" : "Add Item"}</button>
              {editingByobId && <button type="button" onClick={cancelEditByob} data-testid="bf-cancel" className="w-full text-sm text-[#8A8175] hover:text-[#3A3632] py-1">Cancel</button>}
            </form>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3" data-testid="admin-byob-list">
              {byob.map((b) => (
                <div key={b.id} className={`bg-white rounded-xl border p-4 flex items-center gap-3 ${editingByobId === b.id ? "border-[#8E9A86]" : "border-[#E5DFD5]"}`}>
                  <img src={b.image || PLACEHOLDER_IMG} alt="" className="h-12 w-12 rounded-lg object-cover bg-[#F2EFEB]" />
                  <div className="flex-1 min-w-0"><div className="font-medium text-[#3A3632] text-sm truncate">{b.name}</div><div className="text-xs text-[#8A8175]">{money(b.price)}{b.category ? ` · ${b.category}` : ""}</div></div>
                  <button onClick={() => startEditByob(b)} data-testid={`edit-byob-${b.id}`} className="text-[#8A8175] hover:text-[#8E9A86]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => delByob(b.id)} data-testid={`del-byob-${b.id}`} className="text-[#8A8175] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "blog" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <AdminBlogForm blogForm={blogForm} setBlogForm={setBlogForm} editingPostId={editingPostId} onSubmit={savePost} onCancel={cancelEditPost} />
            <div className="lg:col-span-2 space-y-3" data-testid="admin-blog-list">
              {posts.length === 0 ? <p className="p-8 text-center text-[#8A8175]">No posts yet — write your first above.</p> : posts.map((p) => (
                <div key={p.id} className={`bg-white rounded-xl border p-4 flex items-center gap-4 ${editingPostId === p.id ? "border-[#8E9A86]" : "border-[#E5DFD5]"}`}>
                  <img src={p.image || PLACEHOLDER_IMG} alt="" className="h-14 w-14 rounded-lg object-cover bg-[#F2EFEB]" />
                  <div className="flex-1"><div className="font-medium text-[#3A3632]">{p.title}</div><div className="text-sm text-[#8A8175] line-clamp-1">{p.excerpt} {p.video_url && "· 🎬 video"}</div></div>
                  <button onClick={() => startEditPost(p)} data-testid={`edit-post-${p.slug}`} className="text-[#8A8175] hover:text-[#8E9A86]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => delPost(p.id)} data-testid={`del-post-${p.slug}`} className="text-[#8A8175] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "video" && (
          <div className="max-w-2xl">
            <form onSubmit={saveVideo} data-testid="admin-video-form" className="bg-white rounded-xl border border-[#E5DFD5] p-6 space-y-3">
              <h3 className="font-heading text-xl text-[#3A3632] flex items-center gap-2"><Video className="h-4 w-4" /> Homepage Welcome Video</h3>
              <p className="text-sm text-[#6B655F]">Paste a YouTube or Vimeo link. It will appear in a beautiful section on your homepage. Leave blank to hide it.</p>
              <input placeholder="https://www.youtube.com/watch?v=…" value={welcomeVideo} onChange={(e) => setWelcomeVideo(e.target.value)} className="ai" data-testid="video-url" />
              <button type="submit" data-testid="video-submit" className="bg-[#8E9A86] text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#7A8772]">Save Video</button>
            </form>

            <form onSubmit={saveImpact} data-testid="admin-impact-form" className="bg-white rounded-xl border border-[#E5DFD5] p-6 space-y-3 mt-6">
              <h3 className="font-heading text-xl text-[#3A3632] flex items-center gap-2"><HeartHandshake className="h-4 w-4" /> Homepage Impact Counter</h3>
              <p className="text-sm text-[#6B655F]">This is your <strong>starting number</strong> of families helped. Every paid order is <strong>automatically added</strong> on top of it, so the homepage counter keeps growing on its own. It animates up from zero when visitors scroll to it.</p>
              <input type="number" min="0" placeholder="e.g. 4" value={impactFamilies} onChange={(e) => setImpactFamilies(e.target.value)} className="ai" data-testid="impact-families-input" />
              <button type="submit" data-testid="impact-submit" className="bg-[#8E9A86] text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#7A8772]">Save Starting Number</button>
            </form>

            <div className="bg-white rounded-xl border border-[#E5DFD5] p-6 mt-6" data-testid="admin-hope-cards-panel">
              <h3 className="font-heading text-xl text-[#3A3632] flex items-center gap-2"><Sparkles className="h-4 w-4" /> Hope Cards Launch</h3>
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${hopeLive ? "bg-[#8E9A86]/15 text-[#7A8772]" : "bg-[#F2EFEB] text-[#8A8175]"}`} data-testid="hope-live-status">
                  {hopeLive ? "● Live & shoppable" : "○ Coming soon (teaser)"}
                </span>
              </div>
              <p className="text-sm text-[#6B655F] mt-3 flex items-center gap-2"><BellRing className="h-4 w-4 text-[#C4A4A4]" /> Waitlist: <strong>{waitlist.total}</strong> total · <strong>{waitlist.pending}</strong> not yet notified</p>
              <p className="text-xs text-[#8A8175] mt-2">Launching makes the Hope Cards page shoppable (individual + Collector's Set at 20% off) and emails everyone on the waitlist who hasn't been notified yet.</p>
              <div className="flex gap-3 mt-4">
                {!hopeLive ? (
                  <button onClick={launchHopeCards} data-testid="launch-hope-cards" className="bg-[#8E9A86] text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#7A8772] inline-flex items-center gap-2"><Sparkles className="h-4 w-4" /> Launch Hope Cards</button>
                ) : (
                  <>
                    <button onClick={launchHopeCards} data-testid="notify-waitlist" className="border border-[#8E9A86] text-[#8E9A86] rounded-full px-5 py-2.5 font-medium hover:bg-[#8E9A86] hover:text-white transition-colors inline-flex items-center gap-2"><BellRing className="h-4 w-4" /> Email {waitlist.pending} pending</button>
                    <button onClick={unpublishHopeCards} data-testid="unpublish-hope-cards" className="text-[#8A8175] hover:text-[#3A3632] text-sm">Set back to coming soon</button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "audience" && (
          <div data-testid="admin-audience">
            <div className="grid sm:grid-cols-4 gap-4 mb-6">
              {[["Subscribers", audience.total], ["Emails queued", audience.queued], ["Emails sent", audience.sent], ["Sources", Object.keys(audience.by_source || {}).length]].map(([label, val]) => (
                <div key={label} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
                  <div className="font-heading text-3xl text-[#8E9A86]">{val}</div>
                  <div className="text-xs text-[#8A8175] mt-1">{label}</div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-[#E5DFD5] p-5 mb-4">
              <div className="text-sm font-medium text-[#3A3632] mb-2">Active funnels</div>
              <p className="text-xs text-[#6B655F] leading-relaxed">Welcome series (signup) · Browse reminder (viewed product) · Abandoned cart (started checkout) · Post-purchase follow-up · Subscription welcome + renewal · Win-back. All send automatically via email.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden" data-testid="admin-subscribers-list">
              {(audience.subscribers || []).length === 0 ? <p className="p-8 text-center text-[#8A8175]">No subscribers yet.</p> : audience.subscribers.map((s) => (
                <div key={s.id} className="p-3 px-4 border-b border-[#E5DFD5] last:border-0 flex justify-between items-center">
                  <div className="text-sm text-[#3A3632]">{s.email}</div>
                  <span className="text-[11px] bg-[#F2EFEB] text-[#8A8175] rounded-full px-2.5 py-0.5">{s.source}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <>
          {impact && (
            <div className="mb-5" data-testid="admin-impact-section">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="inline-flex rounded-full bg-[#F2EFEB] p-1" data-testid="impact-range-toggle">
                  {[["all", "All Time"], ["month", "This Month"]].map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => selectImpactRange(val)}
                      className={`text-sm px-4 py-1.5 rounded-full transition-colors ${impactRange === val ? "bg-white text-[#3A3632] shadow-sm" : "text-[#8A8175]"}`}
                      data-testid={`impact-range-${val}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={exportImpactCsv}
                  className="inline-flex items-center gap-2 text-sm px-4 py-1.5 rounded-full border border-[#E5DFD5] text-[#3A3632] hover:bg-[#F2EFEB] transition-colors"
                  data-testid="impact-export-csv"
                >
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-impact-widget">
              <div className="bg-white rounded-xl border border-[#E5DFD5] p-4">
                <div className="text-[11px] uppercase tracking-wide text-[#8A8175]">Hope Kits sent</div>
                <div className="font-heading text-3xl text-[#8E9A86] mt-1" data-testid="impact-kits-total">{impact.kits_total.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5DFD5] p-4">
                <div className="text-[11px] uppercase tracking-wide text-[#8A8175]">Warm vs School</div>
                <div className="font-heading text-2xl text-[#3A3632] mt-1" data-testid="impact-split">{impact.kits_warm} <span className="text-sm text-[#8A8175]">warm</span> · {impact.kits_school} <span className="text-sm text-[#8A8175]">school</span></div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5DFD5] p-4">
                <div className="text-[11px] uppercase tracking-wide text-[#8A8175]">Donations</div>
                <div className="font-heading text-3xl text-[#C4A4A4] mt-1" data-testid="impact-donations">${impact.donations_total.toFixed(2)}</div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5DFD5] p-4">
                <div className="text-[11px] uppercase tracking-wide text-[#8A8175]">Outreach orders</div>
                <div className="font-heading text-3xl text-[#3A3632] mt-1" data-testid="impact-orders">{impact.outreach_orders.toLocaleString()}</div>
              </div>
            </div>
            </div>
          )}
          <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden" data-testid="admin-orders-list">
            {orders.length === 0 ? <p className="p-8 text-center text-[#8A8175]">No orders yet.</p> : orders.map((o) => (
              <div key={o.id} className="p-4 border-b border-[#E5DFD5] last:border-0 flex justify-between items-center gap-3">
                <div className="min-w-0"><div className="font-medium text-[#3A3632] truncate">{o.summary || "Order"}</div><div className="text-xs text-[#8A8175]">{o.email || "guest"} · {new Date(o.created_at).toLocaleString()}</div>{o.promo_gift && (<span data-testid={`gift-badge-${o.id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium bg-[#F3EEF7] text-[#5B3A78] border border-[#D8C7E6] rounded-full px-2.5 py-0.5"><Heart className="h-3 w-3 fill-[#5B3A78]" /> Add free gift: {o.promo_gift}</span>)}
                  {Array.isArray(o.grace_deliveries) && o.grace_deliveries.length > 0 && (
                    <div className="mt-2 space-y-1.5" data-testid={`grace-deliveries-${o.id}`}>
                      {o.grace_deliveries.map((g, gi) => (
                        <div key={gi} className="text-[11px] bg-[#8E9A86]/8 border border-[#8E9A86]/25 rounded-lg px-2.5 py-1.5">
                          <div className="text-[#3A3632] font-medium">{(g.product || "Kit").replace("Hope in Action · ", "")} × {g.quantity || 1}</div>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 bg-[#3A3632]/8 text-[#3A3632]">{g.fulfillment === "bulk" ? "Bulk via partner" : (g.address || "").startsWith("Ship to buyer") ? "Ship to buyer" : "Ship to recipient"}</span>
                            {g.color && <span className="inline-flex items-center rounded-full px-2 py-0.5 bg-[#C4A4A4]/15 text-[#B09090]">Color: {g.color}</span>}
                            {g.includes_card && <span className="inline-flex items-center rounded-full px-2 py-0.5 bg-[#8E9A86]/15 text-[#7A8772]">✎ Hope Card</span>}
                          </div>
                          {g.address && <div className="text-[#8A8175] mt-1">{g.address}</div>}
                          {g.note && <div className="mt-1 text-[#3A3632] font-hand text-sm bg-white border border-dashed border-[#C4A4A4]/50 rounded px-2 py-1">“{g.note}”</div>}
                        </div>
                      ))}
                    </div>
                  )}</div>
                <div className="flex items-center gap-3 shrink-0">
                  {o.has_physical !== false && !o.kind && (
                    <button onClick={() => printHopeCard(o.id)} data-testid={`print-hope-card-${o.id}`}
                      className="text-xs inline-flex items-center gap-1 border border-[#8E9A86] text-[#8E9A86] rounded-full px-3 py-1.5 font-medium hover:bg-[#8E9A86] hover:text-white transition-colors">
                      <Printer className="h-3.5 w-3.5" /> Hope Card
                    </button>
                  )}
                  {o.gift_receipt && (
                    <button onClick={() => printGiftReceipt(o.id)} data-testid={`print-gift-receipt-${o.id}`}
                      className="text-xs inline-flex items-center gap-1 border border-[#C4A4A4] text-[#B09090] rounded-full px-3 py-1.5 font-medium hover:bg-[#C4A4A4] hover:text-white transition-colors">
                      <Gift className="h-3.5 w-3.5" /> Gift Receipt
                    </button>
                  )}
                  <div className="text-right"><div className="font-semibold text-[#8E9A86]">{money(o.amount)}</div><span className={`text-xs px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-[#8E9A86]/15 text-[#7A8772]" : "bg-[#F2EFEB] text-[#8A8175]"}`}>{o.payment_status}</span></div>
                </div>
              </div>
            ))}
          </div>
          </>
        )}

        {tab === "programs" && (
          <div className="space-y-6" data-testid="admin-programs-list">
            <ProgramConfigEditor />
            <div className="pt-2 border-t border-[#E5DFD5]" />
            <h3 className="font-heading text-lg text-[#3A3632]">Quote Requests (CRM)</h3>
            <div className="bg-[#FBF7F0] border border-[#E5DFD5] rounded-xl px-5 py-3 text-sm text-[#6B655F]">
              Program quote requests from Hospital, Church &amp; Corporate. Orders under $500 self-serve to checkout automatically; orders $500+ appear here as <strong>pending approval</strong> — approve to email a payment link, or deny to invite the customer to adjust &amp; resubmit.
            </div>
            {programLeads.length === 0 ? <p className="p-8 text-center text-[#8A8175]">No program inquiries yet.</p> : programLeads.map((l) => {
              const badge = { pending_approval: ["Pending approval", "#C4A4A4"], approved: ["Approved · link sent", "#8E9A86"], paid: ["Paid", "#8E9A86"], denied: ["Denied", "#B0846F"], self_serve: ["Self-serve", "#8A8175"], checkout_started: ["Checkout started", "#8A8175"] }[l.status] || [l.status, "#8A8175"];
              return (
              <div key={l.id} className="bg-white rounded-xl border border-[#E5DFD5] p-5" data-testid={`program-lead-${l.id}`}>
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <div className="font-medium text-[#3A3632]">{l.company} <span className="text-sm text-[#8A8175]">· {l.contact_name} · {l.email}{l.phone ? ` · ${l.phone}` : ""}</span></div>
                    <div className="text-sm text-[#8E9A86] mt-0.5">{l.program_title}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-2xl text-[#3A3632]">${Number(l.amount).toFixed(2)}</div>
                    <span className="text-[11px] rounded-full px-2.5 py-0.5 text-white" style={{ background: badge[1] }}>{badge[0]}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-[#6B655F]">
                  {(l.quote?.lines || []).map((ln, i) => <span key={i}>{ln.question}: <strong className="text-[#3A3632]">{ln.choice}</strong></span>)}
                </div>
                {l.notes && <p className="text-sm text-[#6B655F] mt-2 italic">"{l.notes}"</p>}
                <div className="text-xs text-[#8A8175] mt-2">{new Date(l.created_at).toLocaleString()}</div>
                {l.status === "pending_approval" && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => approveLead(l.id)} data-testid={`approve-lead-${l.id}`} className="inline-flex items-center gap-1.5 bg-[#8E9A86] text-white rounded-full px-4 py-1.5 text-sm hover:bg-[#7A8772]"><Check className="w-4 h-4" /> Approve &amp; send payment link</button>
                    <button onClick={() => denyLead(l.id)} data-testid={`deny-lead-${l.id}`} className="inline-flex items-center gap-1.5 bg-white border border-[#E5DFD5] text-[#3A3632] rounded-full px-4 py-1.5 text-sm hover:bg-[#F2EFEB]"><X className="w-4 h-4" /> Deny &amp; invite to adjust</button>
                  </div>
                )}
              </div>
            );})}
          </div>
        )}
        {tab === "contacts" && (
          <div className="space-y-3" data-testid="admin-contacts-list">
            {contacts.length === 0 ? <p className="p-8 text-center text-[#8A8175]">No messages yet.</p> : contacts.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
                <div className="flex justify-between"><div className="font-medium text-[#3A3632]">{c.name} <span className="text-sm text-[#8A8175]">· {c.email}</span></div><span className="text-xs text-[#8A8175]">{new Date(c.created_at).toLocaleDateString()}</span></div>
                {c.subject && <div className="text-sm text-[#8E9A86] mt-1">{c.subject}</div>}
                {(c.program || c.organization || c.phone) && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {c.program && <span className="text-[11px] bg-[#8E9A86]/12 text-[#7A8772] rounded-full px-2.5 py-0.5">{c.program}</span>}
                    {c.organization && <span className="text-[11px] bg-[#F2EFEB] text-[#6B655F] rounded-full px-2.5 py-0.5">🏢 {c.organization}</span>}
                    {c.phone && <span className="text-[11px] bg-[#F2EFEB] text-[#6B655F] rounded-full px-2.5 py-0.5">📞 {c.phone}</span>}
                  </div>
                )}
                <p className="text-[#6B655F] mt-2 text-sm">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`.ai{width:100%;border:1px solid #D1CCC5;border-radius:0.5rem;padding:0.55rem 0.75rem;font-size:0.9rem;outline:none}.ai:focus{border-color:#8E9A86;box-shadow:0 0 0 2px rgba(142,154,134,.25)}`}</style>
    </div>
  );
}
